import { queryOptions, useQuery } from '@tanstack/react-query'
import { ApiError, apiFetch } from '@app/lib/api'
import {
  canViewCari,
  canViewPurchasing,
  LEAST_PRIVILEGED_ROLE,
  type Role,
} from '../../../worker/api/auth.contract'
import type {
  AuthUser,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  MeResponse,
} from '../../../worker/api/auth.types'

/**
 * Session data. Response types come from the Worker contract
 * (`worker/api/auth.types.ts`), so the screen and the API share one set of types.
 */

export type { AuthUser } from '../../../worker/api/auth.types'

export const authKeys = {
  session: ['auth', 'me'] as const,
}

/** A 401 is not an error here but "no session" — the guard decides accordingly. */
export async function fetchSession(): Promise<AuthUser | null> {
  try {
    const { user } = await apiFetch<MeResponse>('/auth/me')
    return user
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export const sessionQuery = queryOptions({
  queryKey: authKeys.session,
  queryFn: fetchSession,
  // A session lives 30 days; no need to ask again on every navigation.
  staleTime: 5 * 60_000,
  retry: false,
})

/**
 * The signed-in account's role, or the least privileged one while the session
 * is still unknown.
 *
 * It reads the session already in the cache, so it costs no request. The
 * fallback is what makes the two hooks below answer `false` before the session
 * lands: they decide whether a screen draws a link, and a link that appears and
 * then turns into text is worse than one that arrives a frame late.
 */
function useRole(): Role {
  const { data: user } = useQuery(sessionQuery)
  return user?.role ?? LEAST_PRIVILEGED_ROLE
}

/*
 * "May this user open cari / satınalma?" — one hook per module, for screens
 * that merely link into them (see components/entity-link.tsx) rather than
 * living in them. Separate because the answers differ: a `purchasing` account
 * gets satınalma and not cari. Both are rendering hints only — the Worker is
 * what actually refuses the data.
 */

export function useCanViewCari(): boolean {
  return canViewCari(useRole())
}

export function useCanViewPurchasing(): boolean {
  return canViewPurchasing(useRole())
}

export async function login(credentials: LoginRequest): Promise<AuthUser> {
  const { user } = await apiFetch<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  })
  return user
}

export function logout(): Promise<LogoutResponse> {
  return apiFetch<LogoutResponse>('/auth/logout', { method: 'POST' })
}
